import { useRef, useState } from 'react';
import { uploadClientLogo } from '../../lib/clientLogo';
import { saveClientLogoPath } from '../../lib/clientLogoDb';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import { invalidateClientLogoCache } from '../../lib/clientLogoCache';
import type { Client } from '../../types/crm';
import { AccountAvatar } from './AccountAvatar';

interface AccountLogoUploadProps {
  client: Client;
  canEdit: boolean;
  onUpdated: () => void;
}

export function AccountLogoUpload({ client, canEdit, onUpdated }: AccountLogoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);
  /** Shown immediately after upload even if DB save is delayed (schema cache). */
  const [previewPath, setPreviewPath] = useState<string | null>(null);

  const displayClient: Client = {
    ...client,
    logo_path: previewPath ?? client.logo_path,
  };

  async function handleFile(file: File) {
    if (!file.type.startsWith('image/') && !/\.(png|jpe?g|webp|gif|heic|heif)$/i.test(file.name)) {
      setError('Please choose an image file (PNG, JPG, HEIC, etc.).');
      return;
    }

    setUploading(true);
    setError(null);
    setWarn(null);

    try {
      const path = await uploadClientLogo(client.id, file);
      setPreviewPath(path);
      invalidateClientLogoCache(client.logo_path);
      invalidateClientLogoCache(path);

      const { error: saveError, usedRpc } = await saveClientLogoPath(client.id, path);
      if (saveError) {
        setWarn(
          formatSupabaseError(saveError, 'storage') +
            ' The image was uploaded to Storage; refresh may be needed after fixing the database.',
        );
        onUpdated();
        return;
      }

      if (usedRpc) {
        setWarn(
          'Logo saved via database function. If lists do not update, run 025_postgrest_reload_and_logo_rpc.sql in Supabase, then refresh.',
        );
      }

      onUpdated();
    } catch (err) {
      setPreviewPath(null);
      setError(formatSupabaseError(err, 'storage'));
    } finally {
      setUploading(false);
    }
  }

  const hint = displayClient.logo_path ? 'Change logo' : 'Upload logo';

  return (
    <div className="account-logo-upload">
      {canEdit ? (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,image/png,image/jpeg,image/jpg,image/webp,image/gif,image/heic,image/heif"
            className="account-logo-upload__input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="account-logo-upload__trigger"
            disabled={uploading}
            title={hint}
            aria-label={hint}
            onClick={() => inputRef.current?.click()}
          >
            <AccountAvatar client={displayClient} size="lg" />
            <span className="account-logo-upload__overlay" aria-hidden>
              {uploading ? '…' : hint}
            </span>
          </button>
        </>
      ) : (
        <AccountAvatar client={displayClient} size="lg" />
      )}
      {warn && <p className="field-hint account-logo-upload__warn">{warn}</p>}
      {error && <p className="field-hint account-logo-upload__error">{error}</p>}
    </div>
  );
}
